// src/App.tsx
import React, { lazy, ReactNode, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useNavigate, useLocation, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { RefreshCw } from 'lucide-react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CsrfProvider, useCsrf } from './context/CsrfContext';
import { setCsrfToken } from './services/apiClient';
import ErrorBoundary from './components/Common/ErrorBoundary';
import api from './services/api';
import Navbar from './components/Layout/Navbar';
import DashboardLayout from './components/Layout/DashboardLayout';
import { trackMetaEvent } from './utils/metaPixelHelper';
import { authDestination, normalizeChallengePath, normalizeDeploymentPath } from './utils/deploymentIntent';

import AdminRoute from './components/Auth/AdminRoute';

// Context Providers
import { ToastProvider } from './context/ToastContext';
import { ServiceProvider } from './context/ServiceContext';

// Footer
import Footer from './components/Footer/Footer';

// Common Components
import ScrollToTop from './components/Common/ScrollToTop';

// Route components are loaded on demand so public visitors do not download the
// complete dashboard, admin, marketplace, documentation, and hackathon UIs in
// the initial bundle.
const Login = lazy(() => import('./components/Auth/Login'));
const OAuthConsent = lazy(() => import('./components/Auth/OAuthConsent'));
const Register = lazy(() => import('./components/Auth/Register'));
const ForgotPassword = lazy(() => import('./components/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/Auth/ResetPassword'));
const VerifyEmail = lazy(() => import('./components/Auth/VerifyEmail'));
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'));
const Profile = lazy(() => import('./components/Profile/Profile'));
const SessionManager = lazy(() => import('./components/Profile/SessionManager'));
const WalletPage = lazy(() => import('./components/Billing/WalletPage'));
const StarterCheckout = lazy(() => import('./components/Billing/StarterCheckout'));
const BudgetSettings = lazy(() => import('./components/Billing/BudgetSettings'));
const RateLimitMonitor = lazy(() => import('./components/Dashboard/RateLimitMonitor'));
const BuildQueue = lazy(() => import('./components/Dashboard/BuildQueue'));
const ContainerStats = lazy(() => import('./components/Dashboard/ContainerStats'));
const ServiceDetailPage = lazy(() => import('./components/Dashboard/ServiceDetailPage'));
const ProjectOverview = lazy(() => import('./components/Dashboard/ProjectOverview'));
const PlanApproval = lazy(() => import('./components/Dashboard/PlanApproval'));
const ApiDocsPage = lazy(() => import('./components/Documentation/ApiDocsPage'));
const ComplianceDashboard = lazy(() => import('./components/Dashboard/ComplianceDashboard'));
const AuditLogViewer = lazy(() => import('./components/Dashboard/AuditLogViewer'));
const DataPrivacySettings = lazy(() => import('./components/Profile/DataPrivacySettings'));
const AgentsPage = lazy(() => import('./components/Agents/AgentsPage'));
const AdminDashboard = lazy(() => import('./components/Admin/AdminDashboard'));
const NetworksPage = lazy(() => import('./components/Networks/NetworksPage'));
const CustomDomains = lazy(() => import('./components/Domains/CustomDomains'));
const LandingPage = lazy(() => import('./components/Pages/LandingPageNew'));
const PricingPage = lazy(() => import('./components/Pages/PricingPage'));
const ServicesPage = lazy(() => import('./components/Pages/ServicesPage'));
const About = lazy(() => import('./components/Pages/About'));
const Contact = lazy(() => import('./components/Pages/Contact'));
const BlogIndex = lazy(() => import('./components/Pages/BlogIndex'));
const TermsOfService = lazy(() => import('./components/Pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./components/Pages/PrivacyPolicy'));
const McpPrivacyPolicy = lazy(() => import('./components/Pages/McpPrivacyPolicy'));
const RefundPolicy = lazy(() => import('./components/Pages/RefundPolicy'));
const ShippingDelivery = lazy(() => import('./components/Pages/ShippingDelivery'));
const Hackathon = lazy(() => import('./components/Pages/Hackathon'));
const HackathonRegister = lazy(() => import('./components/Pages/HackathonRegister'));
const HackathonFAQ = lazy(() => import('./components/Pages/HackathonFAQ'));
const HackathonRules = lazy(() => import('./components/Pages/HackathonRules'));
const HackathonSchedule = lazy(() => import('./components/Pages/HackathonSchedule'));
const HackathonAdmin = lazy(() => import('./components/Pages/HackathonAdmin'));
const HackathonDashboard = lazy(() => import('./components/Pages/HackathonDashboard'));
const HackathonGallery = lazy(() => import('./components/Pages/HackathonGallery'));
const HackathonProject = lazy(() => import('./components/Pages/HackathonProject'));
const HackathonWinners = lazy(() => import('./components/Pages/HackathonWinners'));
const HackathonFinalists = lazy(() => import('./components/Pages/HackathonFinalists'));
const HackathonJudge = lazy(() => import('./components/Pages/HackathonJudge'));
const DocsPageNew = lazy(() => import('./components/Pages/DocsPageNew'));
const ManagedRedis = lazy(() => import('./components/Product/ManagedRedis'));
const AiSearchPage = lazy(() => import('./components/AiSearch/AiSearchPage'));
const CampaignContentNotice = lazy(() => import('./components/Pages/CampaignContentNotice'));
const DeployGuidePage = lazy(() => import('./components/Seo/DeployGuides'));
const DeployGuidesIndex = lazy(() => import('./components/Seo/DeployGuides').then((m) => ({ default: m.DeployGuidesIndex })));
const ComparePage = lazy(() => import('./components/Seo/Compare'));
const CompareIndex = lazy(() => import('./components/Seo/Compare').then((m) => ({ default: m.CompareIndex })));
const ManagedPaasIndia = lazy(() => import('./components/Seo/ManagedPaasIndia'));
const StatusPage = lazy(() => import('./components/Status/StatusPage'));
const CareersPage = lazy(() => import('./components/Pages/CareersPage'));
const InternshipAdmin = lazy(() => import('./components/Pages/InternshipAdmin'));
const CertificateVerification = lazy(() => import('./components/Pages/CertificateVerification'));
const CompanyDocumentVerification = lazy(() => import('./components/Pages/CompanyDocumentVerification'));

// ... all your existing imports end here

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
    fbq: (...args: any[]) => void;
  }
}


// paste this block right before:
// "function ProtectedRoute({ children }..."

function GARouteTracker(): null {
  const location = useLocation();

  useEffect(() => {
    // Google Analytics page tracking
    const gaId = process.env.REACT_APP_GA_MEASUREMENT_ID;
    if (gaId && typeof window.gtag !== 'undefined') {
      window.gtag('config', gaId, {
        page_path: location.pathname + location.search,
      });
    }
    // Meta Pixel SPA page tracking
    if (typeof window.fbq !== 'undefined') {
      window.fbq('track', 'PageView');
    }
  }, [location]);

  return null;
}

// Protected route wrapper: redirects to /login if not authenticated
function ProtectedRoute({ children }: { children: ReactNode }): React.ReactElement {
  const { user, loading } = useAuth();
  const location = useLocation();
  const requestedPath = location.pathname + location.search;
  const intent = normalizeDeploymentPath(requestedPath) || normalizeChallengePath(requestedPath);

  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to={intent ? `/login?next=${encodeURIComponent(intent)}` : '/login'} replace />;
  return <>{children}</>;
}

// Guest route wrapper: redirects to /dashboard if already authenticated
function GuestRoute({ children }: { children: ReactNode }): React.ReactElement {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingSpinner />;
  if (user) return <Navigate to={authDestination(location.search)} replace />;
  return <>{children}</>;
}

// Handle OAuth callback: extracts token from URL params, stores it, then redirects
function OAuthCallback(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, fetchTeams } = useAuth();
  const token = searchParams.get('token');
  const isNewUser = searchParams.get('is_new_user') === 'true';

  React.useEffect(() => {
    const handleOAuthToken = async () => {
      if (token) {
        try {
          api.setToken(token);
          // Fetch user data to update AuthContext
          const data = await api.getMe();
          setUser(data.user);
          await fetchTeams();

          // Fire Meta Pixel CompleteRegistration if this was a new OAuth signup
          if (isNewUser) {
            trackMetaEvent('CompleteRegistration');
          }

          navigate(authDestination(), { replace: true });
        } catch (error) {
          console.error('OAuth callback error:', error);
          localStorage.removeItem('token');
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };
    
    handleOAuthToken();
  }, [token, isNewUser, navigate, setUser, fetchTeams]);

  return <LoadingSpinner />;
}

function LoadingSpinner(): React.ReactElement {
  return (
    <div className="min-h-screen bg-root flex items-center justify-center">
      <div className="text-center">
        <RefreshCw className="w-12 h-12 animate-spin text-indigo mx-auto mb-4" />
        <div className="text-xl text-body">Loading...</div>
      </div>
    </div>
  );
}

// /vs/<provider> was the old comparison URL; aws had a page there but has no
// verified comparison yet, so it goes to the index.
function LegacyCompareRedirect(): React.ReactElement {
  const { provider } = useParams();
  const known = ['railway', 'render', 'heroku', 'digitalocean-app-platform', 'fly-io', 'vercel'];
  return <Navigate to={provider && known.includes(provider) ? `/compare/${provider}` : '/compare'} replace />;
}

// Layout wrapper for public pages
function PublicPage({ children }: { children: ReactNode }): React.ReactElement {
  return <div id="main-content" tabIndex={-1}>{children}</div>;
}

function AppRoutes(): React.ReactElement {
  const { loading } = useAuth();

  if (loading) return <LoadingSpinner />;

  return (
    <Routes>
      {/* Public pages */}
      <Route path="/" element={<PublicPage><LandingPage /></PublicPage>} />
      <Route path="/pricing" element={<PublicPage><PricingPage /></PublicPage>} />
      <Route path="/plans/starter/checkout" element={<ProtectedRoute><StarterCheckout /></ProtectedRoute>} />
      <Route path="/services" element={<PublicPage><ServicesPage /></PublicPage>} />
      <Route path="/about" element={<PublicPage><About /></PublicPage>} />
      <Route path="/contact" element={<PublicPage><Contact /></PublicPage>} />
      <Route path="/careers" element={<PublicPage><CareersPage /></PublicPage>} />
      <Route path="/verify/internship/:code" element={<PublicPage><CertificateVerification /></PublicPage>} />
      <Route path="/verify/document/:code" element={<PublicPage><CompanyDocumentVerification /></PublicPage>} />
      <Route path="/blog" element={<PublicPage><BlogIndex /></PublicPage>} />
      <Route path="/terms-of-service" element={<PublicPage><TermsOfService /></PublicPage>} />
      <Route path="/privacy-policy" element={<PublicPage><PrivacyPolicy /></PublicPage>} />
      {/* Referenced as policy_uri by the MCP connector and in its directory listing. */}
      <Route path="/mcp-privacy" element={<PublicPage><McpPrivacyPolicy /></PublicPage>} />
      <Route path="/refund-policy" element={<PublicPage><RefundPolicy /></PublicPage>} />
      <Route path="/cancellation-policy" element={<PublicPage><RefundPolicy /></PublicPage>} />
      <Route path="/shipping-delivery" element={<PublicPage><ShippingDelivery /></PublicPage>} />
      
      {/* Hackathon Pages */}
      <Route path="/hackathon" element={<PublicPage><Hackathon /></PublicPage>} />
      {/* Legacy program pages advertised obsolete dates and unapproved rewards.
          Keep old links safe without presenting them as part of Challenge 2026. */}
      <Route path="/hackathon/ambassador" element={<Navigate to="/hackathon" replace />} />
      <Route path="/hackathon/volunteer" element={<Navigate to="/hackathon" replace />} />
      <Route path="/hackathon/register" element={<ProtectedRoute><HackathonRegister /></ProtectedRoute>} />
      <Route path="/hackathon/dashboard" element={<ProtectedRoute><HackathonDashboard /></ProtectedRoute>} />
      <Route path="/hackathon/judge" element={<ProtectedRoute><HackathonJudge /></ProtectedRoute>} />
      <Route path="/hackathon/projects" element={<PublicPage><HackathonGallery /></PublicPage>} />
      <Route path="/hackathon/projects/:id" element={<PublicPage><HackathonProject /></PublicPage>} />
      <Route path="/hackathon/winners" element={<PublicPage><HackathonWinners /></PublicPage>} />
      <Route path="/hackathon/finalists" element={<PublicPage><HackathonFinalists /></PublicPage>} />
      <Route path="/hackathon/faq" element={<PublicPage><HackathonFAQ /></PublicPage>} />
      <Route path="/hackathon/rules" element={<PublicPage><HackathonRules /></PublicPage>} />
      <Route path="/hackathon/schedule" element={<PublicPage><HackathonSchedule /></PublicPage>} />
      <Route path="/hackathon/admin" element={
        <AdminRoute superAdminOnly>
          <HackathonAdmin />
        </AdminRoute>
      } />
      <Route path="/hackathon/ambassador/dashboard" element={<Navigate to="/hackathon" replace />} />
      
      {/* Documentation - New Vercel-style docs */}
      <Route path="/docs" element={<PublicPage><DocsPageNew /></PublicPage>} />
      <Route path="/docs/:section" element={<PublicPage><DocsPageNew /></PublicPage>} />
      <Route path="/docs/:section/:page" element={<PublicPage><DocsPageNew /></PublicPage>} />
      
      {/* Old docs (keep for now as fallback) */}
      <Route path="/docs-old" element={<Navigate to="/docs" replace />} />

      {/* SEO Content Pages - Comparison */}
      {/* Older /vs/ comparison URLs now point at the verified /compare pages. */}
      <Route path="/vs/:provider" element={<LegacyCompareRedirect />} />
      <Route path="/compare" element={<PublicPage><CompareIndex /></PublicPage>} />
      <Route path="/compare/:slug" element={<PublicPage><ComparePage /></PublicPage>} />
      <Route path="/deploy" element={<PublicPage><DeployGuidesIndex /></PublicPage>} />
      <Route path="/deploy/:slug" element={<PublicPage><DeployGuidePage /></PublicPage>} />
      <Route path="/managed-paas-india" element={<PublicPage><ManagedPaasIndia /></PublicPage>} />
      <Route path="/status" element={<PublicPage><StatusPage /></PublicPage>} />
      <Route path="/redis" element={<PublicPage><ManagedRedis /></PublicPage>} />
      <Route path="/ai-search" element={<PublicPage><AiSearchPage /></PublicPage>} />
      <Route path="/ai-search/check" element={<Navigate to="/ai-search" replace />} />
      <Route path="/cheapest-paas-india" element={<Navigate to="/managed-paas-india" replace />} />

      {/* SEO Content Pages - Blog */}
      <Route path="/blog/:slug" element={<PublicPage><CampaignContentNotice title="Guide being updated" description="This older guide is not part of the campaign until its instructions and product claims have been re-tested against the current DigitalOcean-based platform." /></PublicPage>} />

      {/* SEO Content Pages - Use Cases */}
      <Route path="/use-cases/:slug" element={<PublicPage><CampaignContentNotice title="AI platform work is planned for a later release" description="AI agents, private LLM hosting, firms, suites, and marketplace workflows remain outside the production-ready campaign scope." /></PublicPage>} />
      <Route path="/ai-platform" element={<PublicPage><CampaignContentNotice title="AI platform work is planned for a later release" description="AI agents, private LLM hosting, pipelines, firms, suites, and marketplace workflows are retained in source but excluded from the current production campaign." /></PublicPage>} />

      {/* Auth pages (redirect to dashboard if logged in) */}
      <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />

      {/* MCP connector consent screen. Not wrapped in GuestRoute or
          ProtectedRoute: it handles its own session check so it can bounce to
          login and return to the same authorization request. */}
      <Route path="/oauth/authorize" element={<OAuthConsent />} />
      <Route path="/register" element={<GuestRoute><Register /></GuestRoute>} />
      <Route path="/forgot-password" element={<GuestRoute><ForgotPassword /></GuestRoute>} />
      <Route path="/reset-password" element={<GuestRoute><ResetPassword /></GuestRoute>} />
      {/* verify-email is NOT a GuestRoute: a just-registered user is logged in when they click the link */}
      <Route path="/verify-email" element={<VerifyEmail />} />

      {/* OAuth callback handler */}
      <Route path="/dashboard" element={<OAuthCallbackOrDashboard />} />

      {/* Project overview (Phase 3F) */}
      <Route path="/projects/:id" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ProjectOverview />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Phase 3G: review and approve an infrastructure plan */}
      <Route path="/plans/:id" element={
        <ProtectedRoute>
          <DashboardLayout>
            <PlanApproval />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Service Detail Page */}
      <Route path="/services/:id" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ServiceDetailPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Container Stats Page */}
      <Route path="/services/:serviceId/stats" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ContainerStats />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* API Docs Page */}
      <Route path="/docs/api" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ApiDocsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Profile Page — with sidebar */}
      <Route path="/profile" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Profile />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Session Manager — with sidebar */}
      <Route path="/sessions" element={
        <ProtectedRoute>
          <DashboardLayout>
            <SessionManager />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Wallet / Billing — with sidebar */}
      <Route path="/wallet" element={
        <ProtectedRoute>
          <DashboardLayout>
            <WalletPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Budget Settings — with sidebar */}
      <Route path="/budget" element={
        <ProtectedRoute>
          <DashboardLayout>
            <BudgetSettings />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Custom Domains — with sidebar */}
      <Route path="/dashboard/domains" element={
        <ProtectedRoute>
          <DashboardLayout>
            <CustomDomains projectId={null} globalMode={true} />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Rate Limit Monitor — with sidebar */}
      <Route path="/rate-limits" element={
        <ProtectedRoute>
          <DashboardLayout>
            <RateLimitMonitor />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Build Queue — with sidebar */}
      <Route path="/build-queue" element={
        <ProtectedRoute>
          <DashboardLayout>
            <BuildQueue />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Phase 4: Compliance Dashboard (admin) */}
      <Route path="/compliance" element={
        <ProtectedRoute>
          <AdminRoute permission="compliance.read">
            <DashboardLayout>
              <ComplianceDashboard />
            </DashboardLayout>
          </AdminRoute>
        </ProtectedRoute>
      } />

      {/* Phase 4: Audit Trail Viewer (admin) */}
      <Route path="/compliance/audit" element={
        <ProtectedRoute>
          <AdminRoute permission="security.audit">
            <DashboardLayout>
              <AuditLogViewer />
            </DashboardLayout>
          </AdminRoute>
        </ProtectedRoute>
      } />

      {/* Phase 4: User Privacy Settings (DPDP) */}
      <Route path="/privacy-settings" element={
        <ProtectedRoute>
          <DashboardLayout>
            <DataPrivacySettings />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Deferred AI surfaces remain in source, but are not loaded into the production campaign. */}
      <Route path="/suites/*" element={<Navigate to="/ai-platform" replace />} />
      <Route path="/my-subscriptions" element={<Navigate to="/ai-platform" replace />} />
      <Route path="/admin/suites" element={<Navigate to="/ai-platform" replace />} />

      {/* Phase 6: Admin Dashboard (has its own sidebar) */}
      <Route path="/admin/dashboard" element={
        <ProtectedRoute>
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        </ProtectedRoute>
      } />
      <Route path="/admin/internships" element={
        <ProtectedRoute>
          <AdminRoute superAdminOnly>
            <InternshipAdmin />
          </AdminRoute>
        </ProtectedRoute>
      } />

      {/* AI Agents — connect an AI agent (MCP) and see what it deployed */}
      <Route path="/agents" element={
        <ProtectedRoute>
          <DashboardLayout>
            <AgentsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/templates" element={<Navigate to="/ai-platform" replace />} />

      <Route path="/marketplace/*" element={<PublicPage><CampaignContentNotice title="Marketplace is not yet part of the production core" description="The marketplace UI is retained for future development, but it is not being advertised or offered as a campaign-ready service." /></PublicPage>} />

      {/* Networks Management */}
      <Route path="/networks" element={
        <ProtectedRoute>
          <DashboardLayout>
            <NetworksPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/llm" element={<Navigate to="/ai-platform" replace />} />
      <Route path="/pipelines" element={<Navigate to="/ai-platform" replace />} />

      <Route path="/agent-store/*" element={<PublicPage><CampaignContentNotice title="Agent Store is planned for a later release" description="Prebuilt AI images and agent deployment flows require separate registry migration and production smoke testing before launch." /></PublicPage>} />

      <Route path="/my-agents/*" element={<Navigate to="/ai-platform" replace />} />

      {/* Agent Firms */}
      <Route path="/firms" element={<PublicPage><CampaignContentNotice title="Agent Firms are planned for a later release" description="This future AI orchestration surface is intentionally excluded from the current campaign." /></PublicPage>} />
      <Route path="/my-firms/*" element={<Navigate to="/ai-platform" replace />} />

      {/* Catch-all: redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Handles both OAuth callback (?token=xxx) and normal dashboard access
function OAuthCallbackOrDashboard(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  if (token) {
    return <OAuthCallback />;
  }

  return (
    <ProtectedRoute>
      <DashboardLayout>
        <Dashboard />
      </DashboardLayout>
    </ProtectedRoute>
  );
}

// Pages that should NOT show the footer (sidebar layout pages)
const SIDEBAR_PATHS = ['/dashboard', '/profile', '/wallet', '/services/', '/compliance', '/privacy-settings', '/suites', '/my-subscriptions', '/admin', '/agents', '/docs', '/pipelines', '/marketplace', '/my-agents', '/my-firms', '/networks', '/llm'];

// Component to sync CSRF token with API client
function CsrfSync(): null {
  const { csrfToken } = useCsrf();
  
  useEffect(() => {
    setCsrfToken(csrfToken);
  }, [csrfToken]);
  
  return null;
}

function AppShell(): React.ReactElement {
  const location = useLocation();
  const isSidebarPage = SIDEBAR_PATHS.some(p => location.pathname.startsWith(p));

  return (
    <div className="flex flex-col min-h-screen">
      <GARouteTracker />
      <CsrfSync />
      <Navbar />
      <div className="flex-grow pt-20">
        <Suspense fallback={<LoadingSpinner />}>
          <AppRoutes />
        </Suspense>
      </div>
      {/* Only show footer on public pages, not on sidebar-layout pages */}
      {!isSidebarPage && <Footer />}
    </div>
  );
}

export default function App(): React.ReactElement {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ScrollToTop />
          <CsrfProvider>
            <AuthProvider>
              <ToastProvider>
                <ServiceProvider>
                  <AppShell />
                </ServiceProvider>
              </ToastProvider>
            </AuthProvider>
          </CsrfProvider>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  );
}
